import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import { rentIsZero } from 'helpers/targets';
import { formatAmount, formatAmountValue, sqlDateToLabel } from 'helpers/helpers';

import useStyles from './styles';
import { AccountEditPlan, ChartComputedData } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { Currency } from 'enums';
import { CalculationMode } from 'common/interfaces';

interface Props {
  planData: AccountEditPlan;
  metaData: ChartComputedData;
  currency: Currency;
  original?: boolean;
  detailMode?: boolean;
  briefInfo: boolean;
  originalClass: string;
}

const Overview: React.FC<Props> = ({
  planData,
  metaData,
  original,
  detailMode,
  briefInfo,
  currency,
  originalClass,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ overview: false });

  return rentIsZero(planData.rentData) ? null : (
    <>
      <Grid container className={`${classes.graphInfo} ${original ? originalClass : ''}`}>
        {!briefInfo && (
          <Grid item xs={12} sm={4}>
            {detailMode ? (
              <>
                <Typography>{t('accountPlan_currentValue')}</Typography>
                <Typography variant="h2" className={classes.value}>
                  {planData.balance && formatAmountValue(planData.balance.value, currency)}
                </Typography>
              </>
            ) : (
              <>
                <Typography>{t('accountPlan_startDeposit')}</Typography>
                <Typography variant="h2" className={classes.value}>
                  {planData.initial && formatAmountValue(planData.initial.value, currency)}
                </Typography>
              </>
            )}
          </Grid>
        )}

        <Grid item xs={12} sm={4}>
          {planData.amountAtRelevantDate && (
            <>
              <Typography>
                {planData.rentApplied ? t('accountPlan_rentStartValue') : t('accountPlan_10yearsValue')}
              </Typography>
              <Typography variant="h2" className={classes.value}>
                {formatAmount(planData.amountAtRelevantDate, currency)}
              </Typography>
              <Typography>
                {t(`accountPlan_rentStartValue_inflation${planData.chart.inflation.enabled ? 'On' : 'Off'}`)}
              </Typography>
            </>
          )}
          {metaData.showRange && (
            <>
              {metaData.displaySetValue ? (
                <>
                  <Typography>
                    {t('accountPlan_valueAtDisplay')}
                    {': '}
                    <strong>
                      {metaData.displayDate && sqlDateToLabel(metaData.displayDate!, 'LLLL yyyy')}
                      {metaData.displayAge && ` (${metaData.displayAge} ${t('general_years')})`}
                    </strong>
                  </Typography>
                  <Typography variant="h2" className={classes.value}>
                    {metaData.displayValue && formatAmount({ value: metaData.displayValue! }, currency)}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography>{t('accountPlan_endDate')}</Typography>
                  <Typography variant="h2" className={classes.value}>
                    {metaData.endDate && sqlDateToLabel(metaData.endDate!, 'LLLL yyyy')}
                  </Typography>
                </>
              )}
            </>
          )}
        </Grid>

        <Grid item xs={12} sm={4}>
          {planData.rentData?.portfolioRentCalculationMode && (
            <>
              <Typography>{t('general_rent_monthly')}</Typography>
              <Typography variant="h2" className={classes.value}>
                {planData.rentData.portfolioRentCalculationMode === CalculationMode.CALCULATED &&
                  formatAmountValue(planData.rentData.calculatedPortfolioRent.value, currency)}
                {planData.rentData.portfolioRentCalculationMode === CalculationMode.MANUAL &&
                  formatAmountValue(planData.rentData.plannedPortfolioRent.value, currency)}
                {planData.rentData.portfolioRentCalculationMode === CalculationMode.REQUIRED &&
                  planData.rentData.requiredPortfolioRent &&
                  formatAmountValue(planData.rentData.requiredPortfolioRent.value, currency)}
              </Typography>
              <Typography>
                {t(`accountPlan_rentStartValue_inflationOn`)}
              </Typography>
            </>
          )}
          {metaData.showRange && !metaData.displaySetValue && (
            <>
              <Typography>{t('accountPlan_valueAtTheEnd')}</Typography>
              <Typography variant="h2" className={classes.value}>
                {metaData.endValue && formatAmount({ value: metaData.endValue! }, currency)}
              </Typography>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default React.memo(observer(Overview));
